import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { IonModal } from '@ionic/angular/common';
import { Match } from '../../_shared/models/match.model';
import { RossService } from '../ross/ross.service';
import { FormsModule } from '@angular/forms';
import { MatchService } from '../../_core/services/match.service';
import { ToastService } from '../../_core/services/toast.service';
import { delay, tap } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { PublisherService } from '../../admin/admin-publishers/publisher.service';
import { CommonModule } from '@angular/common';
import { TieDetailsService } from '../../ties/tie-details/tie-details.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-manage-score-subscription',
  templateUrl: './manage-score-subscription.component.html',
  styleUrls: ['./manage-score-subscription.component.scss'],
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule],
})
export class ManageScoreSubscriptionComponent implements OnInit {
  private modalController = inject(ModalController);
  private authService = inject(AuthService);
  private rossService = inject(RossService);
  private publisherService = inject(PublisherService);
  private toastService = inject(ToastService);
  private matchService = inject(MatchService);
  private tieDetailsService = inject(TieDetailsService);
  private translateService = inject(TranslateService);
  @Input({ required: false }) match: Match;
  @Input({ required: false }) tieId: string;
  @Input({ required: false }) tournamentId: string;
  @Input({required: true}) allowSubscribingToScore: boolean = false;
  @ViewChild(IonModal) modal: IonModal;
  isScoreSubscribed: boolean = false;
  partner: string = '';
  allPartners: string[] = [];
  isUserAdmin: boolean = false;
  subscriptionNote:string = "";
  showValidationError: boolean = false;
  validationError: string = ""
  tieScoreSubscribers: string[] = []; // Store tie score subscribers
  tournamentScoreSubscribers: string[] = []; // Store tournament score subscribers
  isLoading: boolean = false;
  

  constructor() { }

  async ngOnInit() {
    const authUser: any = await this.authService.getAuthUser();
    this.isUserAdmin = authUser?.role?.value === 500;
    
    // Initialize tieScoreSubscribers as empty array
    this.tieScoreSubscribers = [];
    this.tournamentScoreSubscribers = [];
    
    // Handle tournament subscriptions if tournamentId is provided
    this.isLoading = true;
    if (this.tournamentId) {
      await this.initializeTournamentSubscription(authUser);
    }
    // Handle tie subscriptions if tieId is provided
    else if (this.tieId) {
      await this.initializeTieSubscription(authUser);
    } else {
      // Handle match subscriptions (existing logic)
      const scoreSubscribers = this.match?.scoreSubscribers || [];
      await this.initializeMatchSubscription(authUser, scoreSubscribers);
    }
  }

  private async initializeTournamentSubscription(authUser: any) {
    // For tournament subscriptions, we need to get the current subscribers from the tournament
    // Call the API to get current tournament score subscribers
    this.rossService.getTournamentScoreSubscribers(this.tournamentId).subscribe({
      next: (response: any) => {
        // Handle the response structure: { success: true, data: { tournamentId: string, scoreSubscribers: string[], count: number } }
        this.tournamentScoreSubscribers = Array.isArray(response?.data?.scoreSubscribers) ? response.data.scoreSubscribers : [];       
        
        
        if (this.isUserAdmin) {
          this.publisherService.getAllSubscriptionPartner().subscribe((response: any) => {
            const fetchedPartners = response.data || [];

            // Merge and remove duplicates
            const extraSubscribers = this.tournamentScoreSubscribers.filter(p => !fetchedPartners.includes(p));
            this.allPartners = [...fetchedPartners, ...extraSubscribers];

            // Auto-select if only one subscriber
            if (this.tournamentScoreSubscribers.length === 1) {
              this.partner = this.tournamentScoreSubscribers[0];
            }

            this.isScoreSubscribed = this.checkTournamentScoreSubscribed(this.tournamentScoreSubscribers);
          });
        } else {
          this.publisherService.getPublishers().subscribe((response: any) => {
            const publishers = response.data || [];

            const matchingPublisher = publishers.find(
              (pub: any) => pub.phone === authUser.phone ||  ( pub.email && authUser.email && pub.email === authUser.email)
            );

            if (matchingPublisher && Array.isArray(matchingPublisher.partnerSubscription)) {
              this.allPartners = matchingPublisher.partnerSubscription
                .map((p: any) => p.partnerName)
                .filter((name: string) => !!name?.trim());
            } else {
              this.allPartners = [this.partner];
            }
            this.isScoreSubscribed = this.checkTournamentScoreSubscribed(this.tournamentScoreSubscribers);
          });
        }       
        this.isLoading = false; 
      },
      complete: () => {
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching tournament score subscribers:', error);
        this.tournamentScoreSubscribers = []; // Ensure it's always an array even on error
        this.isScoreSubscribed = false;
        this.isLoading = false;
      }
    });
  }

  private async initializeTieSubscription(authUser: any) {
    // For tie subscriptions, we need to get the current subscribers from the tie
    // Call the API to get current tie score subscribers
    this.rossService.getTieScoreSubscribers(this.tieId).subscribe({
      next: (response: any) => {
        // Ensure we always have an array
        this.tieScoreSubscribers = Array.isArray(response?.data?.scoreSubscribers) ? response.data.scoreSubscribers : [];
        
        if (this.isUserAdmin) {
          this.publisherService.getAllSubscriptionPartner().subscribe((response: any) => {
            const fetchedPartners = response.data || [];

            // Merge and remove duplicates
            const extraSubscribers = this.tieScoreSubscribers.filter(p => !fetchedPartners.includes(p));
            this.allPartners = [...fetchedPartners, ...extraSubscribers];

            // Auto-select if only one subscriber
            if (this.tieScoreSubscribers.length === 1) {
              this.partner = this.tieScoreSubscribers[0];
            }

            this.isScoreSubscribed = this.checkTieScoreSubscribed(this.tieScoreSubscribers);
            this.isLoading = false;
          });
        } else {
          this.publisherService.getPublishers().subscribe((response: any) => {
            const publishers = response.data || [];
            
            const matchingPublisher = publishers.find(
              (pub: any) => pub.phone === authUser.phone || (pub.email && authUser.email && pub.email === authUser.email)
            );            

            if (matchingPublisher && Array.isArray(matchingPublisher.partnerSubscription)) {
              this.allPartners = matchingPublisher.partnerSubscription
                .map((p: any) => p.partnerName)
                .filter((name: string) => !!name?.trim());
            } else {
              this.allPartners = [this.partner];
            }
            this.isScoreSubscribed = this.checkTieScoreSubscribed(this.tieScoreSubscribers);
            this.isLoading = false;
          });
        }
        
      },
      complete: () => {
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching tie score subscribers:', error);
        this.tieScoreSubscribers = []; // Ensure it's always an array even on error
        this.isScoreSubscribed = false;
        this.isLoading = false;
      }
    });
  }

  private async initializeMatchSubscription(authUser: any, scoreSubscribers: string[]) {
    if (this.isUserAdmin) {
      this.publisherService.getAllSubscriptionPartner().subscribe((response: any) => {
        const fetchedPartners = response.data || [];

        // Merge and remove duplicates
        const extraSubscribers = scoreSubscribers.filter(p => !fetchedPartners.includes(p));
        this.allPartners = [...fetchedPartners, ...extraSubscribers];

        // Auto-select if only one subscriber
        if (scoreSubscribers.length === 1) {
          this.partner = scoreSubscribers[0];
        }

        this.isScoreSubscribed = this.checkScoreSubscribed();
        this.isLoading = false;
      });
    } else {
      this.publisherService.getPublishers().subscribe((response: any) => {
        const publishers = response.data || [];

        const matchingPublisher = publishers.find(
          (pub: any) => pub.phone === authUser.phone || (pub.email && authUser.email && pub.email === authUser.email)
        );
        

        if (matchingPublisher && Array.isArray(matchingPublisher.partnerSubscription)) {
          this.allPartners = matchingPublisher.partnerSubscription
            .map((p: any) => p.partnerName)
            .filter((name: string) => !!name?.trim());
        } else {
          this.allPartners = [this.partner];
        }
        this.isScoreSubscribed = this.checkScoreSubscribed();
        this.isLoading = false;
      });
    }
  }

  async dismiss() {
    this.modalController.dismiss(null, 'cancel');
  }

  handleScoreSubscription(partner: string) {
    if (this.tournamentId) {
      // Handle tournament subscription
      if (!this.tournamentScoreSubscribers.includes(partner)) {
        this.subscribeToTournament(partner);
      } else {
        this.toastService.presentToast(this.translateService.instant('You are already subscribed to this partner\'s score updates.'));
      }
    } else if (this.tieId) {
      // Handle tie subscription
      if (!this.tieScoreSubscribers.includes(partner)) {
        this.subscribeToTie(partner);
      } else {
        this.toastService.presentToast(this.translateService.instant('You are already subscribed to this partner\'s score updates.'));
      }
    } else {
      // Handle match subscription (existing logic)
      if (!this.match.scoreSubscribers.includes(partner)) {
        this.subscribe(partner);
      } else {
        this.toastService.presentToast(this.translateService.instant('You are already subscribed to this partner\'s score updates.'));
      }
    }
  }

  subscribeToTournament(partner: string) {
    this.rossService.subscribeToTournament(this.tournamentId, partner).pipe(
      tap((res: any) => {
        // Add to local tournament score subscribers array
        this.tournamentScoreSubscribers.push(partner);
        
        // Also update match.scoreSubscribers for UI consistency
        if (!this.match.scoreSubscribers) {
          this.match.scoreSubscribers = [];
        }
        this.match.scoreSubscribers.push(partner);
        
        this.toastService.presentToast(res.message);
        this.matchService.publishScoreUpdated(this.match);
      }),
      delay(1000) // wait 1 second before continuing
    ).subscribe(() => {
      this.modalController.dismiss(null, 'cancel');
    });
  }

  subscribeToTie(partner: string) {
    this.rossService.subscribeToTie(this.tieId, partner).pipe(
      tap((res: any) => {
        // Add to local tie score subscribers array
        this.tieScoreSubscribers.push(partner);
        
        // Also update match.scoreSubscribers for UI consistency
        if (!this.match.scoreSubscribers) {
          this.match.scoreSubscribers = [];
        }
        this.match.scoreSubscribers.push(partner);
        
        this.toastService.presentToast(this.translateService.instant(res.message));
        this.matchService.publishScoreUpdated(this.match);
        
        // Refresh tie data to update the tie-details component
        this.refreshTieData();
      }),
      delay(1000) // wait 1 second before continuing
    ).subscribe(() => {
      this.modalController.dismiss(null, 'cancel');
    });
  }

  private refreshTieData() {
    // Call the tie-matches API to refresh tie data
    this.tieDetailsService.getMatches(this.tieId, true).subscribe({
      next: (matches) => {
        //console.log('Tie data refreshed after subscription');
      },
      error: (error) => {
        console.error('Error refreshing tie data:', error);
      }
    });
  }

  subscribe(partner: string) {
    this.rossService.subscribeToMatch(this.match._id, partner).pipe(
      tap((res: any) => {
        this.match.scoreSubscribers.push(partner);
        this.toastService.presentToast(res.message);
        this.matchService.publishScoreUpdated(this.match);
      }),
      delay(1000) // wait 1 second before continuing
    ).subscribe(() => {
      this.modalController.dismiss(null, 'cancel');
    });
  }

  unsubscribeFromTie(partner: string) {
    this.rossService.unsubscribeFromTie(this.tieId, partner).pipe(
      tap((res: any) => {
        this.toastService.presentToast(res.message);

        // Ensure tieScoreSubscribers is an array
        if (!Array.isArray(this.tieScoreSubscribers)) {
          this.tieScoreSubscribers = [];
        }

        // Remove from local tie score subscribers array
        this.tieScoreSubscribers = this.tieScoreSubscribers.filter(p => p !== partner);

        // Also remove from match.scoreSubscribers for UI consistency
        if (!Array.isArray(this.match.scoreSubscribers)) {
          this.match.scoreSubscribers = [];
        }
        this.match.scoreSubscribers = this.match.scoreSubscribers.filter(p => p !== partner);

        this.matchService.publishScoreUpdated(this.match);
        
        // Refresh tie data to update the tie-details component
        this.refreshTieData();
      }),
      delay(1000) // Delay closing the modal by 1 second
    ).subscribe(() => {
      this.modalController.dismiss(null, 'cancel');
    });
  }

  unsubscribeFromTournament(partner: string) {
    this.rossService.unsubscribeFromTournament(this.tournamentId, partner).pipe(
      tap((res: any) => {
        this.toastService.presentToast(res.message);

        // Ensure tournamentScoreSubscribers is an array
        if (!Array.isArray(this.tournamentScoreSubscribers)) {
          this.tournamentScoreSubscribers = [];
        }

        // Remove from local tournament score subscribers array
        this.tournamentScoreSubscribers = this.tournamentScoreSubscribers.filter(p => p !== partner);

        // Also remove from match.scoreSubscribers for UI consistency
        if (!Array.isArray(this.match.scoreSubscribers)) {
          this.match.scoreSubscribers = [];
        }
        this.match.scoreSubscribers = this.match.scoreSubscribers.filter(p => p !== partner);

        this.matchService.publishScoreUpdated(this.match);
      }),
      delay(1000) // Delay closing the modal by 1 second
    ).subscribe(() => {
      this.modalController.dismiss(null, 'cancel');
    });
  }

  unsubscribe(partner: string) {
    this.rossService.unsubscribeFromMatch(this.match._id, partner).pipe(
      tap((res: any) => {
        this.toastService.presentToast(res.message);

        // Remove the partner from the scoreSubscribers array
        this.match.scoreSubscribers = this.match.scoreSubscribers.filter(p => p !== partner);

        this.matchService.publishScoreUpdated(this.match);
      }),
      delay(1000) // Delay closing the modal by 1 second
    ).subscribe(() => {
      this.modalController.dismiss(null, 'cancel');
    });
  }

  checkScoreSubscribed(): boolean {
    const scoreSubscribers = this.match?.scoreSubscribers || [];

    const partnerMatch = !!this.partner && scoreSubscribers.includes(this.partner);
    const anyPartnerMatch =
      (this.allPartners || []).some(p => scoreSubscribers.includes(p)) ||
      (this.isUserAdmin && scoreSubscribers.length > 0);

    return partnerMatch || anyPartnerMatch;
  }

  checkTieScoreSubscribed(tieScoreSubscribers: string[]): boolean {
    const partnerMatch = !!this.partner && tieScoreSubscribers.includes(this.partner);
    const anyPartnerMatch =
      (this.allPartners || []).some(p => tieScoreSubscribers.includes(p)) ||
      (this.isUserAdmin && tieScoreSubscribers.length > 0);

    return partnerMatch || anyPartnerMatch;
  }

  checkTournamentScoreSubscribed(tournamentScoreSubscribers: string[]): boolean {
    const partnerMatch = !!this.partner && tournamentScoreSubscribers.includes(this.partner);
    const anyPartnerMatch =
      (this.allPartners || []).some(p => tournamentScoreSubscribers.includes(p)) ||
      (this.isUserAdmin && tournamentScoreSubscribers.length > 0);

    return partnerMatch || anyPartnerMatch;
  }


  submitAdminSubscription() {
    this.showValidationError = false;
    let partnerTrimmed = this.partner?.trim() || '';
    const noteTrimmed = this.subscriptionNote?.trim() || '';
    if (partnerTrimmed.toLowerCase() === 'select') {
      partnerTrimmed = ''
    }

    // Check if both fields are empty
    if (!partnerTrimmed && !noteTrimmed) {
      this.showValidationError = true;
      this.validationError = this.translateService.instant('Please enter either partner or subscription note.');
      return;
    }

    if (!noteTrimmed && partnerTrimmed?.toLowerCase() === 'select' ) {
      this.showValidationError = true;
      this.validationError = this.translateService.instant('Please select valid partner');
      return;
    }

    // If both entered, check if they are the same
    if (partnerTrimmed && noteTrimmed && partnerTrimmed !== noteTrimmed && partnerTrimmed?.toLocaleLowerCase() !== 'select') {
      this.showValidationError = true;
      this.validationError = this.translateService.instant('Partner and subscription note must be the same if both are entered.');
      return;
    }    

    // Proceed with submission
    const valueToSubmit = partnerTrimmed || noteTrimmed;
    
    this.handleScoreSubscription(valueToSubmit);
  }
}
